'use strict';
var moment = require('moment');
const utilities = require('../utilities');

// SCHEMA STRUCTURE:

// schemaName: {
//   fieldName: {
//     required: Boolean that determines if the field is required (Optional, Default = false),
//     default: Fallback value if field is not valid or if field is required but missing (Optional),
//     isValid: Function that takes in the field's input value and returns true or false based on validity,
//     cleanse: Function that takes in the field's input value and cleanses it (Optional),
//     invalidFallback: Boolean to determine if the default fallback should be used if the field is invalid (Optional, Default = false),
//     missingFallback: Boolean to determine if the default fallback should be used if the field is missing (Optional, Default = true),
//     customError: String that gets returned when validation fails on this field (Optional),
//   }
// }

// Base move schema to validate a move object against
// For info on how to build this object, look at the validateAndCleanseObject utility function
const moveObjSchema = (schemaType, options = {}) => {
  // Build schema object
  const { isGhost = false } = options || {};
  let schema = {
    active: {
      required: true,

      default: 1,
      isValid: val => parseInt(val) === 0 || parseInt(val) === 1,
      cleanse: val => parseInt(val),
    },
    trip_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    plan_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    pickup_time: {
      isValid: val => val && moment(val).isValid() && moment(val) >= moment(),
      cleanse: val => utilities.cleanseTimestamp(moment(val).startOf(`minute`).format()),
      customError: `Please make sure the Pickup Time is a valid timestamp and in the future.`,
    },
    delivery_time: {
      isValid: val => val && moment(val).isValid() && moment(val) >= moment(),
      cleanse: val => utilities.cleanseTimestamp(moment(val).startOf(`minute`).format()),
      customError: `Please make sure the Delivery Time is a valid timestamp and in the future.`,
    },
    pickup_stop_id: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    delivery_stop_id: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    sequence: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    customer_id: {
      required: true,
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    payer_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    tookan_relationship_id: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    class: {
      required: true,
      default: `base`,
      isValid: val => val && typeof val === `string`,
    },
    lane_id: {
      required: true,
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    vehicle_year: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    vehicle_make: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    vehicle_model: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    vehicle_color: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    vehicle_stock: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    vehicle_vin: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    vehicle_image: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    vehicle_odometer: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    // TODO: add custom error for vehicle_gross_weight_lbs over the weight we are allowed to move
    vehicle_gross_weight_lbs: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    auto_assign: {
      required: true,
      default: 0,
      isValid: val => parseInt(val) === 0 || parseInt(val) === 1,
      cleanse: val => parseInt(val),
    },
    lyft_flag: {
      required: true,
      default: 0,
      isValid: val => parseInt(val) === 0 || parseInt(val) === 1,
      cleanse: val => parseInt(val),
    },
    driver_name: {
      isValid: val => val && typeof val === `string`,
    },
    driver_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    move_type: {
      required: true,
      default: `drive`,
      isValid: val => val && typeof val === `string`,
    },
    ride_type: {
      isValid: val => val && typeof val === `string`,
    },
    rate_class_override: {
      required: true,
      default: 0,
      isValid: val => parseInt(val) === 0 || parseInt(val) === 1,
      cleanse: val => parseInt(val),
    },
    return_ride_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    parent_move_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    lyft_trigger_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    move_details: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    status: {
      isValid: val => val && typeof val === `string`,
    },
    cancel_status: {
      isValid: val => val && typeof val === `string`,
    },
    pickup_started: {
      isValid: val => val && moment(val).isValid(),
      cleanse: val => utilities.cleanseTimestamp(val),
    },
    pickup_arrived: {
      isValid: val => val && moment(val).isValid(),
      cleanse: val => utilities.cleanseTimestamp(val),
    },
    pickup_successful: {
      isValid: val => val && moment(val).isValid(),
      cleanse: val => utilities.cleanseTimestamp(val),
    },
    delivery_started: {
      isValid: val => val && moment(val).isValid(),
      cleanse: val => utilities.cleanseTimestamp(val),
    },
    delivery_arrived: {
      isValid: val => val && moment(val).isValid(),
      cleanse: val => utilities.cleanseTimestamp(val),
    },
    delivery_successful: {
      isValid: val => val && moment(val).isValid(),
      cleanse: val => utilities.cleanseTimestamp(val),
    },
    move_failed: {
      isValid: val => val && moment(val).isValid(),
      cleanse: val => utilities.cleanseTimestamp(val),
    },
    tracking_link: {
      isValid: val => val && typeof val === `string`,
    },
    ready_by: {
      required: true,
      default: utilities.nowPlus(90),
      isValid: val => val && moment(val).isValid() && moment(val) >= moment(),
      cleanse: val => utilities.cleanseTimestamp(moment(val).startOf(`minute`).format()),
      customError: `Please make sure the Ready-By time is a valid timestamp and in the future.`,
    },
    deliver_by: {
      isValid: val => val && moment(val).isValid() && moment(val) >= moment(),
      cleanse: val => utilities.cleanseTimestamp(moment(val).startOf(`minute`).format()),
      customError: `Please make sure the Deliver-By time is a valid timestamp and in the future.`,
    },
    tags: {
      isValid: val => val && typeof val === `string`,
    },
    synced_with_tookan: {
      isValid: val => val && moment(val).isValid(),
      cleanse: val => utilities.cleanseTimestamp(val),
    },
    priority: {
      required: true,
      default: 10,
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    actual_pickup_milage: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    actual_delivery_milage: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    manual_flag: {
      required: true,
      default: false,
      isValid: val => typeof val === `boolean`,
    },
    chargeable: {
      required: true,
      default: true,
      isValid: val => typeof val === `boolean`,
    },
    payable: {
      required: false,
      default: true,
      isValid: val => typeof val === `boolean`,
    },
    pinnable: {
      required: true,
      default: false,
      isValid: val => typeof val === `boolean`,
    },
    discount_amount: {
      required: false,
      default: 0,
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    discount_reason: {
      isValid: val => val && typeof val === `string`,
    },
    disputed: {
      required: true,
      default: false,
      isValid: val => typeof val === `boolean`,
    },
    dispute_reason: {
      isValid: val => val && typeof val === `string`,
    },
    reference_num: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    consumer_pickup: {
      required: true,
      default: false,
      isValid: val => typeof val === `boolean`,
    },
    consumer_name: {
      isValid: val => val && typeof val === `string`,
    },
    consumer_phone: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    consumer_at_pickup: {
      required: true,
      default: 0,
      isValid: val => parseInt(val) === 0 || parseInt(val) === 1,
      cleanse: val => parseInt(val),
    },
    consumer_type: {
      isValid: val => val && typeof val === `string`,
    },
    dealer_contact: {
      isValid: val => val && (typeof val === `string` || typeof val === `number`),
    },
    config: {
      isValid: val => val && typeof val === `object`,
    },
    pickup_workflow_data: {
      isValid: val => val && typeof val === `object`,
    },
    delivery_workflow_data: {
      isValid: val => val && typeof val === `object`,
    },
    pickup_workflow_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    delivery_workflow_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    workflowset_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    sla_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    createdBy: {
      isValid: val => val && typeof val === `string`,
    },
  };

  // If schemaType is 'concierge', update the move schema to reflect
  if (schemaType === `concierge` || schemaType === `consumer` || schemaType === `customer`) {
    schema = {
      ...schema,
      vehicle_make: {
        ...schema.vehicle_make,
        required: isGhost ? false : true,
      },
      vehicle_model: {
        ...schema.vehicle_model,
        required: isGhost ? false : true,
      },
      consumer_pickup: {
        ...schema.consumer_pickup,
        default: true,
      },
      consumer_name: {
        ...schema.consumer_name,
        required: true,
      },
      consumer_phone: {
        ...schema.consumer_phone,
      },
      consumer_type: {
        ...schema.consumer_type,
        required: true,
        default: `customer`,
      },
    };
  }

  // If schemaType is 'loaner', update the move schema to reflect
  else if (schemaType === `loaner`) {
    schema = {
      ...schema,
      chargeable: {
        ...schema.chargeable,
        default: false,
      },
      consumer_pickup: {
        ...schema.consumer_pickup,
        default: true,
      },
      consumer_name: {
        ...schema.consumer_name,
        required: true,
      },
      consumer_phone: {
        ...schema.consumer_phone,
      },
      consumer_type: {
        ...schema.consumer_type,
        required: true,
        default: `loaner`,
      },
    };
  }

  // Else if schemaType is none of the above, update the move schema to reflect
  else {
    schema = {
      ...schema,
      vehicle_make: {
        ...schema.vehicle_make,
        required: isGhost ? false : true,
      },
      vehicle_model: {
        ...schema.vehicle_model,
        required: isGhost ? false : true,
      },
    };
  }

  // Return schema object
  return schema;
};

// Schema for the outer-most layer of the request object
const requestSchema = (schemaType, options = {}) => {
  const { isConciergePickup = false } = options || {};
  let schema = {
    ready_by_time_utc: {
      isValid: val => val && moment(val).isValid() && moment(val) >= moment(),
      customError: `Please make sure 'ready_by_time_utc' is a valid timestamp and in the future.`,
    },
    duplicate_check: {
      isValid: val => val && typeof val === `string` && val.length <= 80,
    },
  };
  if (schemaType === `concierge` && isConciergePickup) {
    schema = {
      appointment_time_utc: {
        required: true,
        isValid: val => val && moment(val).isValid() && moment(val) >= moment().add(90, `minutes`),
        customError: `Please make sure 'appointment_time_utc' is a valid timestamp and at least 90 minutes in the future. Try time: ${moment()
          .add(95, `minutes`)
          .format('YYYY-MM-DD[T]HH:mm')}.`,
      },
      customer_name: {
        required: true,
        isValid: val => val && typeof val === `string` && val.length <= 80,
      },
      customer_phone: {
        isValid: val => val && typeof val === `string` && val.length <= 80,
      },
      customer_action: {
        required: true,
        isValid: val => val === `pickup` || val === `return`,
        customError: `Please make sure 'customer_action' is either 'pickup' (picking up customer's vehicle) or 'return' (returning customer's vehicle).`,
      },
      duplicate_check: {
        isValid: val => val && typeof val === `string` && val.length <= 80,
      },
    };
  }
  if (schemaType === `concierge` && !isConciergePickup) {
    schema = {
      appointment_time_utc: {
        required: true,
        isValid: val => val && moment(val).isValid() && moment(val) >= moment(),
        customError: `Please make sure 'appointment_time_utc' is a valid timestamp and in the future.`,
      },
      customer_name: {
        required: true,
        isValid: val => val && typeof val === `string` && val.length <= 80,
      },
      customer_phone: {
        isValid: val => val && typeof val === `string` && val.length <= 80,
      },
      customer_action: {
        required: true,
        isValid: val => val === `pickup` || val === `return`,
        customError: `Please make sure 'customer_action' is either 'pickup' (picking up customer's vehicle) or 'return' (returning customer's vehicle).`,
      },
      duplicate_check: {
        isValid: val => val && typeof val === `string` && val.length <= 80,
      },
    };
  }
  return schema;
};

// Schema for move/move_one/move_two/loaner_move/customer_move objects inside the request
const moveSchema = schemaType => {
  let schema = {
    reference_num: {
      isValid: val => val && typeof val === `string` && val.length <= 80,
    },
    dealer_contact: {
      isValid: val => val && typeof val === `string` && val.length <= 255,
    },
    special_instructions: {
      isValid: val => val && typeof val === `string`,
    },
    workflowset_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
    sla_id: {
      isValid: val => !isNaN(parseInt(val)),
      cleanse: val => parseInt(val),
    },
  };
  return schema;
};

// Schema for vehicle object inside the move object of the request
const vehicleSchema = schemaType => {
  let schema = {
    vin: {
      isValid: val => val && typeof val === `string` && val.length <= 80,
    },
    stock: {
      isValid: val => val && typeof val === `string` && val.length <= 80,
    },
    year: {
      isValid: val => val && typeof val === `string` && val.length <= 80,
    },
    make: {
      required: schemaType === `loaner` ? false : true,
      isValid: val => val && typeof val === `string` && val.length <= 80,
    },
    model: {
      required: schemaType === `loaner` ? false : true,
      isValid: val => val && typeof val === `string` && val.length <= 80,
    },
    color: {
      isValid: val => val && typeof val === `string` && val.length <= 80,
    },
    manual: {
      required: true,
      default: false,
      isValid: val => typeof val === `boolean`,
    },
    gross_weight_lbs: {
      required: false,
      isValid: val => typeof val === `number`,
    },
  };
  return schema;
};

// Schema for pickup_location/delivery_location objects inside the move object of the request
const locationSchema = (schemaType, isFullAddressProvided = false) => {
  let schema = {
    name: {
      isValid: val => val && typeof val === `string` && val.length <= 80,
    },
    full_address: {
      required: isFullAddressProvided ? true : false,
      isValid: val => val && typeof val === `string` && val.length <= 255,
    },
    address_one: {
      required: isFullAddressProvided ? false : true,
      isValid: val => val && typeof val === `string` && val.length <= 255,
    },
    address_two: {
      isValid: val => val && typeof val === `string` && val.length <= 255,
    },
    city: {
      required: isFullAddressProvided ? false : true,
      isValid: val => val && typeof val === `string` && val.length <= 80,
    },
    state: {
      required: isFullAddressProvided ? false : true,
      isValid: val => val && typeof val === `string` && val.length <= 80,
    },
    zip: {
      isValid: val => val && typeof val === `string` && val.length <= 10,
    },
  };
  return schema;
};

// Schema for config object inside the move object of the request
const configSchema = schemaType => {
  let schema = {
    hide_service_location: {
      isValid: val => typeof val === `boolean`,
    },
  };
  return schema;
};

module.exports = { moveObjSchema, requestSchema, moveSchema, vehicleSchema, locationSchema, configSchema };
